import { CurrencyPipe, DatePipe, Location, NgClass } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { CanDirective, CapabilityNames } from 'core';
import { LoadingSpinner, TableColumnInfo, TableView, TypeOfColumn } from 'design-system';
import { finalize } from 'rxjs';
import { WorksheetDetail, WorksheetStatus } from '../../interfaces/worksheet.model';
import { WorksheetsService } from '../../services/worksheets.service';
import {
  ConfirmDialogData,
  receiptsConfirmDialogComponent,
} from '../../components/dialog-components/receipts-confirm-dialog-component/receipts-confirm-dialog-component';
import { formatDateLabel, toDate } from '../../utils/receipt.utils';

interface DraftDetail {
  numeroBrogliaccio: number;
  situazione: string;
  versamentiContanti: {
    idIncasso: number;
    dataIncasso: Date | null;
    cliente: string;
    importoMonete: number;
    importoBanconote: number;
    importoTotale: number;
  }[];
  assegni: {
    idIncasso: number;
    dataIncasso: Date | null;
    cliente: string;
    abi: string;
    cab: string;
    banca: string;
    numeroAssegno: string;
    importo: number;
  }[];
  partiteAssociate: {
    numeroDocumento: string;
    dataDocumento: Date | null;
    dataScadenza: Date | null;
    totaleFattura: number;
    importoAssegnato: number;
    importoResiduo: number;
    idIncasso: number;
  }[];
  acconti: {
    codiceCliente: string;
    cliente: string;
    partitaIva: string;
    importo: number;
    idIncasso: number;
  }[];
  riepilogo: {
    totaleContanti: number;
    totaleAssegni: number;
    totaleGenerale: number;
  };
}

const STATUS_LABELS: Record<string, string> = {
  [WorksheetStatus.Nuovo]: 'Nuovo',
  [WorksheetStatus.Confermato]: 'Confermato',
  [WorksheetStatus.PresoInCarico]: 'Preso in carico',
};

const EMPTY_DETAIL: DraftDetail = {
  numeroBrogliaccio: 0,
  situazione: '',
  versamentiContanti: [],
  assegni: [],
  partiteAssociate: [],
  acconti: [],
  riepilogo: { totaleContanti: 0, totaleAssegni: 0, totaleGenerale: 0 },
};

@Component({
  selector: 'rev-receipts-brogliacci-detail',
  imports: [
    CanDirective,
    CurrencyPipe,
    DatePipe,
    LoadingSpinner,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    NgClass,
    TableView,
  ],
  templateUrl: './receipts-brogliacci-detail.html',
  styleUrl: './receipts-brogliacci-detail.scss',
})
export class ReceiptsBrogliacciDetail implements OnInit {
  private readonly service = inject(WorksheetsService);
  private readonly route = inject(ActivatedRoute);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);

  constructor(private readonly location: Location) {}

  readonly CAPS = CapabilityNames;

  idBrogliaccio = '';
  isLoading = false;
  isSaving = false;

  dettaglio: DraftDetail = EMPTY_DETAIL;

  versamentiContantiTable: Record<string, unknown>[] = [];
  assegniTable: Record<string, unknown>[] = [];
  partiteAssociateTable: Record<string, unknown>[] = [];
  accontiTable: DraftDetail['acconti'] = [];

  // Conferma ed elimina esistono solo finche' il brogliaccio e' Nuovo: da
  // Confermato in poi puo' essere gia' in una distinta e il backend rifiuta.
  get puoConfermare(): boolean {
    return this.dettaglio.situazione === STATUS_LABELS[WorksheetStatus.Nuovo];
  }

  get puoEliminare(): boolean {
    return this.puoConfermare;
  }

  statusBadgeClass(status: string): string {
    switch (status) {
      case STATUS_LABELS[WorksheetStatus.PresoInCarico]:
        return 'draft-status-info';
      case STATUS_LABELS[WorksheetStatus.Nuovo]:
        return 'draft-status-warning';
      case STATUS_LABELS[WorksheetStatus.Confermato]:
        return 'draft-status-success';
      default:
        return '';
    }
  }

  readonly versamentiContantiColumns: TableColumnInfo[] = [
    { columnNameDef: 'idIncasso', label: 'ID Incasso', type: TypeOfColumn.TEXT },
    { columnNameDef: 'dataIncasso', label: 'Data', type: TypeOfColumn.TEXT },
    { columnNameDef: 'cliente', label: 'Cliente', type: TypeOfColumn.TEXT },
    {
      columnNameDef: 'importoMonete', label: 'Monete', type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
    {
      columnNameDef: 'importoBanconote', label: 'Banconote', type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
    {
      columnNameDef: 'importoTotale', label: 'Totale', type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
  ];

  readonly assegniColumns: TableColumnInfo[] = [
    { columnNameDef: 'idIncasso', label: 'ID Incasso', type: TypeOfColumn.TEXT },
    { columnNameDef: 'dataIncasso', label: 'Data', type: TypeOfColumn.TEXT },
    { columnNameDef: 'cliente', label: 'Cliente', type: TypeOfColumn.TEXT },
    { columnNameDef: 'abi', label: 'ABI', type: TypeOfColumn.TEXT },
    { columnNameDef: 'cab', label: 'CAB', type: TypeOfColumn.TEXT },
    { columnNameDef: 'banca', label: 'Banca', type: TypeOfColumn.TEXT },
    { columnNameDef: 'numeroAssegno', label: 'N° Assegno', type: TypeOfColumn.TEXT },
    {
      columnNameDef: 'importo', label: 'Importo', type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
  ];

  readonly partiteAssociateColumns: TableColumnInfo[] = [
    { columnNameDef: 'numeroDocumento', label: 'N. Documento', type: TypeOfColumn.TEXT },
    { columnNameDef: 'dataDocumento', label: 'Data documento', type: TypeOfColumn.TEXT },
    { columnNameDef: 'dataScadenza', label: 'Data scadenza', type: TypeOfColumn.TEXT },
    {
      columnNameDef: 'totaleFattura', label: 'Totale fattura', type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
    {
      columnNameDef: 'importoAssegnato', label: 'Assegnato', type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
    {
      columnNameDef: 'importoResiduo', label: 'Residuo', type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
    { columnNameDef: 'idIncasso', label: 'Da incasso', type: TypeOfColumn.TEXT },
  ];

  readonly accontiColumns: TableColumnInfo[] = [
    { columnNameDef: 'codiceCliente', label: 'Codice cliente', type: TypeOfColumn.TEXT },
    { columnNameDef: 'cliente', label: 'Cliente', type: TypeOfColumn.TEXT },
    { columnNameDef: 'partitaIva', label: 'Partita IVA', type: TypeOfColumn.TEXT },
    {
      columnNameDef: 'importo', label: 'Importo', type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
    { columnNameDef: 'idIncasso', label: 'Da incasso', type: TypeOfColumn.TEXT },
  ];

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const routeId = params.get('idBrogliaccio');

      if (!routeId) {
        console.error('ID brogliaccio mancante in URL');
        return;
      }

      this.idBrogliaccio = routeId;
      this.loadDetail();
    });
  }

  loadDetail(): void {
    this.isLoading = true;

    this.service
      .getWorksheetDetail(this.idBrogliaccio)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: detail => {
          if (!detail) {
            this.snackBar.open('Brogliaccio non trovato.', 'Chiudi', { duration: 5000 });
            this.tornaIndietro();
            return;
          }

          this.dettaglio = this.toDetail(detail);
          this.versamentiContantiTable = this.dettaglio.versamentiContanti.map(versamento => ({
            ...versamento,
            dataIncasso: formatDateLabel(versamento.dataIncasso),
          }));
          this.assegniTable = this.dettaglio.assegni.map(assegno => ({
            ...assegno,
            dataIncasso: formatDateLabel(assegno.dataIncasso),
          }));
          this.partiteAssociateTable = this.dettaglio.partiteAssociate.map(partita => ({
            ...partita,
            dataDocumento: formatDateLabel(partita.dataDocumento),
            dataScadenza: formatDateLabel(partita.dataScadenza),
          }));
          this.accontiTable = this.dettaglio.acconti;
        },
        error: err => {
          console.error('Errore caricamento brogliaccio', err);
          this.snackBar.open('Caricamento del brogliaccio non riuscito.', 'Chiudi', {
            duration: 5000,
          });
        },
      });
  }

  conferma(): void {
    if (!this.puoConfermare || this.isSaving) {
      return;
    }

    this.isSaving = true;

    this.service
      .confirmWorksheet(this.idBrogliaccio)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => this.loadDetail(),
        error: err => {
          console.error('Errore conferma brogliaccio', err);
          this.snackBar.open('Conferma non riuscita.', 'Chiudi', { duration: 5000 });
        },
      });
  }

  elimina(): void {
    if (this.isSaving || !this.puoEliminare) {
      return;
    }

    const data: ConfirmDialogData = { message: 'Vuoi eliminare il brogliaccio corrente?' };

    this.dialog
      .open<receiptsConfirmDialogComponent, ConfirmDialogData, boolean>(
        receiptsConfirmDialogComponent,
        { data },
      )
      .afterClosed()
      .subscribe(confermato => {
        if (confermato) {
          this.eseguiEliminazione();
        }
      });
  }

  private eseguiEliminazione(): void {
    this.isSaving = true;

    this.service
      .deleteWorksheet(this.idBrogliaccio)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => this.tornaIndietro(),
        error: err => {
          console.error('Errore eliminazione brogliaccio', err);
          this.snackBar.open('Eliminazione non riuscita.', 'Chiudi', { duration: 5000 });
        },
      });
  }

  tornaIndietro(): void {
    this.location.back();
  }

  private toDetail(detail: WorksheetDetail): DraftDetail {
    const totaleContanti = detail.totaleValuta ?? 0;
    const totaleAssegni = detail.totaleAssegni ?? 0;

    return {
      numeroBrogliaccio: detail.numeroBrogliaccio,
      situazione: STATUS_LABELS[detail.statoBrogliaccio] ?? detail.statoBrogliaccio,
      versamentiContanti: (detail.versamentiContante ?? []).map(versamento => ({
        idIncasso: versamento.numeroIncasso ?? 0,
        dataIncasso: toDate(versamento.dataIncasso),
        cliente: versamento.ragioneSocialeCliente ?? '',
        importoMonete: versamento.monete ?? 0,
        importoBanconote: versamento.banconote ?? 0,
        importoTotale: versamento.totale ?? 0,
      })),
      assegni: (detail.assegni ?? []).map(assegno => ({
        idIncasso: assegno.numeroIncasso ?? 0,
        dataIncasso: toDate(assegno.dataIncasso),
        cliente: assegno.ragioneSocialeCliente ?? '',
        abi: assegno.abi ?? '',
        cab: assegno.cab ?? '',
        banca: assegno.nomeBanca ?? '',
        numeroAssegno: assegno.numeroAssegno ?? '',
        importo: assegno.importo ?? 0,
      })),
      partiteAssociate: (detail.partiteAssociate ?? []).map(partita => ({
        numeroDocumento: partita.numeroDocumento ?? '',
        dataDocumento: toDate(partita.dataDocumento),
        dataScadenza: toDate(partita.dataScadenza),
        totaleFattura: partita.totaleFattura ?? 0,
        importoAssegnato: partita.importoAssegnato ?? 0,
        importoResiduo: partita.importoResiduo ?? 0,
        idIncasso: partita.numeroIncasso ?? 0,
      })),
      acconti: (detail.acconti ?? []).map(acconto => ({
        codiceCliente: acconto.codiceClienteErp ?? '',
        cliente: acconto.ragioneSociale ?? '',
        partitaIva: acconto.partitaIva ?? '',
        importo: acconto.importo ?? 0,
        idIncasso: acconto.numeroIncasso ?? 0,
      })),
      riepilogo: {
        totaleContanti,
        totaleAssegni,
        totaleGenerale: detail.totale ?? totaleContanti + totaleAssegni,
      },
    };
  }
}
